import React, { ChangeEvent } from 'react';

import { Button, Input, Label, Select } from '@thefarmersfront/kitchen';

import { useShallow } from 'zustand/react/shallow';

import { useModal } from '~/hooks/common';
import { useProductModalOptions } from '~/hooks/product';
import { useProductStore } from '~/store';

import { ProductDetailsModalStockInfoSectionSearchProductCodeModal } from '../ProductDetailsModalStockInfoSectionSearchProductCodeModal';

import type { TypeProductModalRequiredInputState } from '~/types/product.types';

import styles from './ProductDetailsModalStockInfoSectionDefault.module.scss';

const ProductDetailsModalStockInfoSectionDefault = ({ hasTempData }: { hasTempData: boolean }) => {
  const productModalOptions = useProductModalOptions();

  const [
    productDetailModalValue,
    setProductDetailModalValue,
    productModalRequiredValueStatus,
    resetProductModalRequiredValueStatus,
    setFocusFieldRefs,
  ] = useProductStore(
    useShallow((state) => [
      state.productDetailModalValue,
      state.setProductDetailModalValue,
      state.productModalRequiredValueStatus,
      state.resetProductModalRequiredValueStatus,
      state.setFocusFieldRefs,
    ])
  );

  const productCodeSearchModal = useModal();

  const handleClickRequiredInput = (e: React.MouseEvent) => {
    resetProductModalRequiredValueStatus(e.currentTarget.id as keyof TypeProductModalRequiredInputState);
  };

  const handleChangeAlternativeCodeInput = (e: ChangeEvent<HTMLInputElement>) => {
    setProductDetailModalValue({
      ...productDetailModalValue,
      alternativeCode: e.target.value,
    });

    resetProductModalRequiredValueStatus('alternativeCode');
  };

  const handleBlurInput = (e: React.FocusEvent<HTMLInputElement>) => {
    setProductDetailModalValue({
      ...productDetailModalValue,
      [e.currentTarget.id]: e.target.value.trim(),
    });
  };

  return (
    <>
      <div className={styles['item-row']}>
        <Label className={styles['label']} title="상품 운영 종류" required />
        <div className={styles['input-wrapper']}>
          <Select
            ref={(el) => setFocusFieldRefs('productType', el)}
            id="productType"
            options={productModalOptions?.productOperationType ?? []}
            disabled
            size="small"
            status={productModalRequiredValueStatus.productType.status}
            validationMessage={productModalRequiredValueStatus.productType.message}
            value={productDetailModalValue.productType}
          />
        </div>
      </div>

      {productDetailModalValue.productType?.value === 'ORIGINAL' && (
        <div className={styles['item-row']}>
          <Label className={styles['label']} title="상품 바코드" required />
          <div className={styles['alt-code-input-wrapper']}>
            <Input
              ref={(el) => setFocusFieldRefs('alternativeCode', el)}
              id="alternativeCode"
              className={styles['input']}
              value={productDetailModalValue.alternativeCode}
              type="text"
              size="small"
              name="barcode"
              count
              maxLength={50}
              placeholder="상품 바코드(88코드) 입력"
              disabled={hasTempData}
              status={productModalRequiredValueStatus.alternativeCode.status}
              validationMessage={productModalRequiredValueStatus.alternativeCode.message}
              onClick={handleClickRequiredInput}
              onChange={handleChangeAlternativeCodeInput}
              onBlur={handleBlurInput}
            />
          </div>
        </div>
      )}

      {productDetailModalValue.productType?.value === 'ALTERNATIVE' && (
        <div className={styles['item-row']}>
          <Label className={styles['label']} title="출하코드" required />
          <div className={styles['product-code-input-wrapper']}>
            <Input
              ref={(el) => setFocusFieldRefs('productCode', el)}
              id="productCode"
              className={styles['input']}
              type="text"
              size="small"
              value={productDetailModalValue.productCode}
              status={productModalRequiredValueStatus.productCode.status}
              validationMessage={productModalRequiredValueStatus.productCode.message}
              disabled
            />
            <Button
              className={styles['button']}
              size="small"
              color="secondary"
              disabled={hasTempData}
              onClick={productCodeSearchModal.handleOpenModal}
            >
              조회
            </Button>
          </div>
        </div>
      )}

      {productCodeSearchModal.isOpen && (
        <ProductDetailsModalStockInfoSectionSearchProductCodeModal
          open={productCodeSearchModal.isOpen}
          onCancel={productCodeSearchModal.handleCloseModal}
          onConfirm={productCodeSearchModal.handleConfirmModal}
        />
      )}
    </>
  );
};

export default ProductDetailsModalStockInfoSectionDefault;
